/* eslint-disable */
import * as signalR from '@microsoft/signalr'
import settings from '@nobia/zeus-components/lib/settings'

import {
  isAuthTimeoutError,
  onAuthRefreshSuccess,
  refreshAccessToken,
  refreshIfSessionExpired,
} from './auth419Recovery'
import {
  canAttemptSignalRStart,
  shouldEnsureHubSubscribe,
} from './signalrAuthGuards'

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000]

class Connector {
  #connection = null

  #messageListeners = new Map()

  #reconnectListeners = new Map()

  #nextListenerId = 0

  #hubSubscribed = false

  #listenersRegistered = false

  #authExpired = false

  #removeRefreshListener = null

  constructor() {
    console.log(
      'SignalR: Checking the user login state before creating the connection'
    )
    this.#removeRefreshListener = onAuthRefreshSuccess(() => {
      this.#authExpired = false
      if (this.#hasAnyListeners()) {
        this.start().catch(console.error)
      }
    })
  }

  #createConnection() {
    if (this.#connection) {
      return
    }
    this.#connection = new signalR.HubConnectionBuilder()
      .withUrl(`${settings.myProjectApi.slice(0, -4)}/notification/`, {
        accessTokenFactory: async () => {
          await refreshIfSessionExpired()
          return window.__OMNI__?.access_token
        },
      })
      .configureLogging(signalR.LogLevel.Debug)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (isAuthTimeoutError(retryContext.retryReason)) {
            this.#handleAuthTimeout()
            return null
          }
          const index = Math.min(
            retryContext.previousRetryCount,
            RECONNECT_DELAYS_MS.length - 1
          )
          return RECONNECT_DELAYS_MS[index]
        },
      })
      .build()

    if (!this.#listenersRegistered) {
      this.#setupListeners()
      this.#listenersRegistered = true
    }

    console.log('SignalR: created connector')
  }

  #handleAuthTimeout() {
    this.#authExpired = true
    refreshAccessToken().catch(() => {})
  }

  async #start() {
    if (!canAttemptSignalRStart(this.#authExpired)) {
      return false
    }

    this.#createConnection()
    if (this.#connection?.state !== signalR.HubConnectionState.Disconnected) {
      return this.#connection?.state === signalR.HubConnectionState.Connected
    }

    try {
      await this.#connection?.start()
      console.log('SignalR: started')
      this.#authExpired = false
      return true
    } catch (error) {
      console.error(error)
      if (isAuthTimeoutError(error)) {
        this.#handleAuthTimeout()
      }
      return false
    }
  }

  async #stop() {
    try {
      await this.#connection?.stop()
      console.log('SignalR: stopped')
    } catch (error) {
      console.error(error)
    }
  }

  #setupListeners() {
    this.#connection?.onreconnecting((error) => {
      console.log('SignalR: Connection lost. Reconnecting.', error)
    })

    this.#connection?.onreconnected(async (connectionId) => {
      console.log('SignalR: Reconnected. Connection ID:', connectionId)
      this.#hubSubscribed = false
      this.#authExpired = false
      if (this.#messageListeners.size > 0) {
        await this.#ensureHubSubscribed()
      }
      for (const cb of this.#reconnectListeners.values()) {
        if (typeof cb === 'function') {
          cb()
        }
      }
    })

    this.#connection?.onclose((error) => {
      console.log('SignalR: Connection closed.', error)
      this.#hubSubscribed = false
      if (isAuthTimeoutError(error)) {
        this.#handleAuthTimeout()
      }
    })

    this.#connection?.off('ChatMessage')
    this.#connection?.on('ChatMessage', (data) => {
      console.log('SignalR: Received ChatMessage', data)
      for (const cb of this.#messageListeners.values()) {
        if (typeof cb === 'function') {
          cb(data)
        }
      }
    })
  }

  async #ensureHubSubscribed() {
    if (this.#hubSubscribed || this.#authExpired) {
      return
    }

    if (
      !this.#connection ||
      this.#connection?.state === signalR.HubConnectionState.Disconnected
    ) {
      const started = await this.#start()
      if (!started) {
        return
      }
    }

    while (
      this.#connection?.state === signalR.HubConnectionState.Connecting ||
      this.#connection?.state === signalR.HubConnectionState.Reconnecting
    ) {
      console.log('SignalR: Connection is not ready')
      await sleep(100)
    }

    if (
      !shouldEnsureHubSubscribe({
        hubSubscribed: this.#hubSubscribed,
        authExpired: this.#authExpired,
        connectionState: this.#connection?.state,
        connectedState: signalR.HubConnectionState.Connected,
      })
    ) {
      return
    }

    console.log('SignalR: Subscribe')
    await this.#connection?.send('SubscribeUser', settings.brand)
    this.#hubSubscribed = true
  }

  async #hubUnsubscribe() {
    if (!this.#connection || !this.#hubSubscribed) {
      return
    }
    await this.#connection.send('UnsubscribeUser', settings.brand)
    this.#hubSubscribed = false
  }

  #hasAnyListeners() {
    return this.#messageListeners.size > 0 || this.#reconnectListeners.size > 0
  }

  id() {
    return this.#connection?.connectionId
  }

  state() {
    return this.#connection?.state
  }

  async start() {
    console.log('SignalR: start listening')
    const started = await this.#start()
    if (!started || !this.#connection) {
      return
    }

    if (this.#connection.state !== signalR.HubConnectionState.Connected) {
      return
    }

    if (this.#messageListeners.size > 0) {
      await this.#ensureHubSubscribed()
    }
  }

  async close() {
    if (!this.#connection) return
    this.#connection.off('ChatMessage')
    this.#hubSubscribed = false
    await this.#stop()
    this.#connection = null
    this.#listenersRegistered = false
  }

  onReconnect(callback) {
    const id = ++this.#nextListenerId
    this.#reconnectListeners.set(id, callback)
    return () => {
      this.offReconnect(id)
    }
  }

  offReconnect(listenerId) {
    if (listenerId != null) {
      this.#reconnectListeners.delete(listenerId)
    } else {
      this.#reconnectListeners.clear()
    }
    if (!this.#hasAnyListeners()) {
      this.close().catch(console.error)
    }
  }

  subscribe(callback) {
    const id = ++this.#nextListenerId
    this.#messageListeners.set(id, callback)
    this.#ensureHubSubscribed().catch(console.error)
    return () => {
      this.unsubscribe(id)
    }
  }

  async unsubscribe(listenerId) {
    if (listenerId != null) {
      this.#messageListeners.delete(listenerId)
    } else {
      this.#messageListeners.clear()
    }

    if (this.#messageListeners.size === 0) {
      await this.#hubUnsubscribe()
    }

    if (!this.#hasAnyListeners()) {
      await this.close()
    }
  }
}

export const Connection = new Connector()
